/**
 * CreatePassword Page
 * Allows users to create their password using a token sent via email
 */

import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import productLogo from "../assets/images/country-seal.jpg";
import mlgLogo from "../assets/images/mlg-logo.png";
import { BASE_URL } from "../config/api";
import { showErrorToast, showConfirm } from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import "../styles/pages/CreatePassword.css";

const CreatePassword = () => {
  const navigate = useNavigate();

  // Extract token from raw URL to preserve special characters like + that
  // useSearchParams would decode as spaces
  const getRawToken = (): string => {
    const search = window.location.search;
    const match = search.match(/[?&]token=([^&]*)/);
    return match ? match[1] : "";
  };

  const token = getRawToken();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validatePassword = (): string | null => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(password))
      return "Password must contain at least one uppercase letter";
    if (!/[a-z]/.test(password))
      return "Password must contain at least one lowercase letter";
    if (!/[0-9]/.test(password))
      return "Password must contain at least one number";
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password))
      return "Password must contain at least one special character";
    if (password !== confirmPassword) return "Passwords do not match";
    return null;
  };

  const getPasswordStrength = (): {
    label: string;
    percent: number;
    color: string;
  } => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score++;

    if (score <= 2) return { label: "Weak", percent: 33, color: "#dc2626" };
    if (score <= 4) return { label: "Medium", percent: 66, color: "#f59e0b" };
    return { label: "Strong", percent: 100, color: "#16a34a" };
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSuccessMessage(null);

    if (!token) {
      showErrorToast(
        "Invalid or missing token. Please use the link from your email.",
      );
      return;
    }

    const validationError = validatePassword();
    if (validationError) {
      showErrorToast(validationError);
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Create Password",
      "Are you sure you want to create your password with the provided credentials?",
      "Yes, create",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setIsSubmitting(true);

      const response = await fetch(`${BASE_URL}/Password/set-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          newPassword: password,
          confirmPassword,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(
          error.message ||
            error.title ||
            "Failed to create password. The link may have expired.",
        );
      }

      setSuccessMessage(
        "Password created successfully! You can now sign in with your new password.",
      );
      setPassword("");
      setConfirmPassword("");

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const strength = password ? getPasswordStrength() : null;

  return (
    <main className="auth-body">
      <section className="auth-shell">
        <div className="auth-hero">
          <div className="hero-overlay" />
          <div className="hero-content">
            <div className="hero-logo">
              <img
                src={mlgLogo || undefined}
                alt="Ministry of Local Government"
              />
            </div>
            <h1>Ministry of Local Government</h1>
            <p>Traditional Marriage &amp; Divorce Certificates</p>
            <div className="hero-footnote">Secure Administration Portal</div>
          </div>
        </div>
        <div className="auth-panel">
          <div className="product-logo">
            <img
              className="logo-desktop"
              src={productLogo || undefined}
              alt="MWETANA"
            />
            <img
              className="logo-mobile"
              src={mlgLogo || undefined}
              alt="Ministry of Local Government"
            />
          </div>

          {successMessage ? (
            <div className="create-password-success">
              <div className="success-icon">✓</div>
              <h3>Password Created!</h3>
              <p>{successMessage}</p>
              <p className="redirect-note">Redirecting to sign in...</p>
            </div>
          ) : (
            <form className="login-form" onSubmit={handleSubmit}>
              <h3 className="create-password-title">Create Your Password</h3>
              <p className="create-password-subtitle">
                Please create a secure password for your account.
              </p>

              <div className="form-field">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder=" "
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <label htmlFor="password">New Password</label>
                <button
                  className="toggle-password"
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 5c5.05 0 9.27 3.11 11 7-1.01 2.26-2.81 4.13-5.07 5.35l-1.6-1.6A7.9 7.9 0 0 0 19.7 12c-1.45-2.6-4.35-4.5-7.7-4.5-1.15 0-2.25.22-3.25.62L7.2 6.57A11.6 11.6 0 0 1 12 5Zm-9.3 7a11.3 11.3 0 0 0 4.39 4.7l-1.6 1.6A13.1 13.1 0 0 1 1 12a13.2 13.2 0 0 1 3.54-4.9l1.6 1.6A11.2 11.2 0 0 0 2.7 12Zm9.3-3.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-.36 1.55l-4.7-4.7c.5-.22 1.03-.35 1.56-.35Zm-3.5 3.5c0-.53.13-1.05.35-1.55l4.7 4.7c-.5.22-1.03.35-1.55.35A3.5 3.5 0 0 1 8.5 12Zm-4.32-8.32 16.14 16.14-1.41 1.41-2.24-2.24A11.7 11.7 0 0 1 12 19c-5.05 0-9.27-3.11-11-7a12.7 12.7 0 0 1 4.03-5.1L2.76 4.63 4.18 3.68Z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 5c5.05 0 9.27 3.11 11 7-1.73 3.89-5.95 7-11 7S2.73 15.89 1 12c1.73-3.89 5.95-7 11-7Zm0 2.5c-3.35 0-6.25 1.9-7.7 4.5 1.45 2.6 4.35 4.5 7.7 4.5s6.25-1.9 7.7-4.5c-1.45-2.6-4.35-4.5-7.7-4.5Zm0 1.5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 1.8a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Z" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {password && strength && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div
                      className="strength-fill"
                      style={{
                        width: `${strength.percent}%`,
                        backgroundColor: strength.color,
                      }}
                    />
                  </div>
                  <span
                    className="strength-label"
                    style={{ color: strength.color }}
                  >
                    {strength.label}
                  </span>
                </div>
              )}

              <div className="form-field">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder=" "
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <label htmlFor="confirmPassword">Confirm Password</label>
                <button
                  className="toggle-password"
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 5c5.05 0 9.27 3.11 11 7-1.01 2.26-2.81 4.13-5.07 5.35l-1.6-1.6A7.9 7.9 0 0 0 19.7 12c-1.45-2.6-4.35-4.5-7.7-4.5-1.15 0-2.25.22-3.25.62L7.2 6.57A11.6 11.6 0 0 1 12 5Zm-9.3 7a11.3 11.3 0 0 0 4.39 4.7l-1.6 1.6A13.1 13.1 0 0 1 1 12a13.2 13.2 0 0 1 3.54-4.9l1.6 1.6A11.2 11.2 0 0 0 2.7 12Zm9.3-3.5a3.5 3.5 0 0 1 3.5 3.5 3.5 3.5 0 0 1-.36 1.55l-4.7-4.7c.5-.22 1.03-.35 1.56-.35Zm-3.5 3.5c0-.53.13-1.05.35-1.55l4.7 4.7c-.5.22-1.03.35-1.55.35A3.5 3.5 0 0 1 8.5 12Zm-4.32-8.32 16.14 16.14-1.41 1.41-2.24-2.24A11.7 11.7 0 0 1 12 19c-5.05 0-9.27-3.11-11-7a12.7 12.7 0 0 1 4.03-5.1L2.76 4.63 4.18 3.68Z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 5c5.05 0 9.27 3.11 11 7-1.73 3.89-5.95 7-11 7S2.73 15.89 1 12c1.73-3.89 5.95-7 11-7Zm0 2.5c-3.35 0-6.25 1.9-7.7 4.5 1.45 2.6 4.35 4.5 7.7 4.5s6.25-1.9 7.7-4.5c-1.45-2.6-4.35-4.5-7.7-4.5Zm0 1.5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 1.8a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Z" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Password Requirements */}
              <div className="password-requirements">
                <p className="requirements-title">Password must contain:</p>
                <ul>
                  <li className={password.length >= 8 ? "met" : ""}>
                    At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(password) ? "met" : ""}>
                    One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(password) ? "met" : ""}>
                    One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(password) ? "met" : ""}>
                    One number
                  </li>
                  <li
                    className={
                      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
                        ? "met"
                        : ""
                    }
                  >
                    One special character
                  </li>
                </ul>
              </div>

              <button
                className="primary-btn"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating Password..." : "Create Password"}
              </button>

              <a className="back-to-login" href="/">
                Back to Sign In
              </a>
            </form>
          )}

          <div className="login-powered">
            Powered by <a href="https://www.mwetana.com.lr">MWETANA</a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CreatePassword;
